import { Component, Input, ChangeDetectorRef, Output, EventEmitter, ViewChild, ElementRef, Inject } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';
import { RadioTypesEnum } from '../../enums/radio-types.enum';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';

/**
 * ### Accessibility Usage
 * + ***ariaDescribedBy*** - use this if the button has an external error message, or it needs extra description for screen reader. Use message's id
 * + ***ariaLabelledBy*** - use this to associate an external label with the search field. Use id of the label as this value. It overwrites hiddenLabel
 * + ***hiddenLabel*** - can consider this as ariaLabel. Use this when label is not ideal for screen readers. It replaces label for screen readers
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***name*** - radio buttons should have the same name if they are in the same radio group. Make sure different radio groups have distinct names
 */
@Component({
    selector: 'agpr-radio',
    templateUrl: './radio.component.html',
    styleUrls: ['./radio.component.scss'],
    providers: [getProvider(RadioComponent)],
    standalone: false
})
export class RadioComponent extends ControlValueAccessorModel<any> {
    readonly agprComponentStyle = AgprComponentStyleEnum;

    @Input() declare value: any;
    @Input() name: string;
    @Input() disabled: boolean;
    @Input() label: string;
    @Input() hiddenLabel: string;
    @Input() ariaLabelledBy: string = '';
    @Input() ariaDescribedBy: string = '';
    @Input() tabindex: number;
    @Input() id: string;
    @Input() tooltip: string;
    @Input() icon: string;
    @Input() defaultFAIconPrefix: boolean = true;
    @Input() iconImageUrl: string;
    @Input() iconImageAltText: string;
    @Input() iconTooltip: string;
    @Input() tooltipPosition: string ='top';
    @Input() style: RadioTypesEnum = RadioTypesEnum.normal;

    @Input() agprStyle: AgprComponentStyleEnum = AgprComponentStyleEnum.social;

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onClick: EventEmitter<any> = new EventEmitter();
    @ViewChild('rb', {static: true}) inputViewChild: ElementRef;


    public checked: boolean;
    public focused: boolean;

    public radioTypesEnum = RadioTypesEnum;

    constructor(
        private cd: ChangeDetectorRef,
        @Inject(APP_BASE_HREF) public appBaseHref: string) {
        super();
    }

    /**
     * Handles click event on label or visible radio button icon
     */
    handleClick(event: MouseEvent, radioButton: HTMLElement, focus: boolean): void {
        event.preventDefault();

        if (this.disabled) {
            return;
        }

        this.select();

        if (focus) {
            radioButton.focus();
        }
    }

    select() {
        if (!this.disabled) {
            this.onClick.emit(null);
            this.inputViewChild.nativeElement.checked = true;
            this.checked = true;
            this.onModelChange(this.value);
        }
    }

    writeValue(value: any): void {
        this.checked = (value === this.value);

        if (this.inputViewChild.nativeElement) {
            this.inputViewChild.nativeElement.checked = this.checked;
        }

        this.cd.markForCheck();
    }


    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    /**
     * Handles focus event
     */
    onFocus(event) {
        this.focused = true;
    }

    /**
     * Handles blur event
     */
    onBlur(event) {
        this.focused = false;
        this.onModelTouched();
    }

    onChange(event) {
        this.select();
    }
}
